import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, Col, Empty, Form, Input, Row, Spin, Typography } from 'antd'
import Button from 'components/Button'
import RichTextEditor from 'components/RichTextEditor'
import useClient from 'hooks/useClient'
import { PERMISSIONS } from 'utils/constants'
import {
  createTermsConditions,
  getTermsConditions,
  updateTermsConditions,
} from 'redux/actions/termsConditions'

const { Text } = Typography

const TERM_TYPES = {
  GLOBAL: 'GLOBAL',
  SUBSCRIPTION: 'SUBSCRIPTION',
}

const TERM_LABELS = {
  GLOBAL: 'Global Terms & Conditions',
  SUBSCRIPTION: 'Subscription Terms & Conditions',
}

const buildDefaultState = () => ({
  [TERM_TYPES.GLOBAL]: {
    id: null,
    type: TERM_TYPES.GLOBAL,
    titleEn: '',
    titleAr: '',
    textEn: '',
    textAr: '',
  },
  [TERM_TYPES.SUBSCRIPTION]: {
    id: null,
    type: TERM_TYPES.SUBSCRIPTION,
    titleEn: '',
    titleAr: '',
    textEn: '',
    textAr: '',
  },
})

const TermsConditions = () => {
  const dispatch = useDispatch()
  const { isClientCan } = useClient()
  const canRead = isClientCan(PERMISSIONS.READ_TERMS_CONDITIONS)
  const canCreate = isClientCan(PERMISSIONS.CREATE_TERMS_CONDITIONS)
  const canUpdate = isClientCan(PERMISSIONS.UPDATE_TERMS_CONDITIONS)
  const { items: termsList = [], isGetLoading, isChangeLoading } = useSelector(
    (state) => state.termsConditions,
  )

  const [state, setState] = useState(buildDefaultState())

  useEffect(() => {
    if (canRead) {
      dispatch(getTermsConditions())
    }
  }, [canRead, dispatch])

  useEffect(() => {
    const initialState = buildDefaultState()

    termsList.forEach((item) => {
      const type = item.type || TERM_TYPES.GLOBAL
      if (!initialState[type]) return

      initialState[type] = {
        id: item.id,
        type,
        titleEn: item.titleEn || item.title || '',
        titleAr: item.titleAr || '',
        textEn: item.textEn || item.text || '',
        textAr: item.textAr || '',
      }
    })

    setState(initialState)
  }, [termsList])

  const sections = useMemo(() => [TERM_TYPES.GLOBAL, TERM_TYPES.SUBSCRIPTION], [])

  const onFieldChange = (type, key, value) => {
    setState((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [key]: value,
      },
    }))
  }

  const onSave = (type) => {
    const payload = state[type]
    if (payload.id) {
      dispatch(
        updateTermsConditions({
          id: payload.id,
          data: payload,
        }),
      )
      return
    }

    dispatch(
      createTermsConditions({
        data: payload,
      }),
    )
  }

  if (!canRead) {
    return <Empty description="You do not have permission to view Terms & Conditions" />
  }

  if (isGetLoading) {
    return (
      <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}>
        <Spin />
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h3 className="page-title">Terms & Conditions</h3>
          <Text type="secondary">Manage global and subscription terms.</Text>
        </div>
      </div>
      <div className="page-wrapper">
        <Row gutter={[16, 16]}>
          {sections.map((type) => {
            const current = state[type]
            const canSave = current.id ? canUpdate : canCreate

            return (
              <Col span={24} key={type}>
                <Card title={TERM_LABELS[type]}>
                  <Form layout="vertical">
                    <Row gutter={[16, 0]}>
                      <Col span={12}>
                        <Form.Item label="Title (EN)" required>
                          <Input
                            value={current.titleEn}
                            onChange={(e) => onFieldChange(type, 'titleEn', e.target.value)}
                            placeholder="English title"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Title (AR)" required>
                          <Input
                            dir="rtl"
                            style={{ textAlign: 'right' }}
                            value={current.titleAr}
                            onChange={(e) => onFieldChange(type, 'titleAr', e.target.value)}
                            placeholder="Arabic title"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Text (EN)" required>
                          <RichTextEditor
                            value={current.textEn}
                            onChange={(html) => onFieldChange(type, 'textEn', html)}
                            placeholder="English terms (rich text)"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Text (AR)" required>
                          <RichTextEditor
                            rtl
                            value={current.textAr}
                            onChange={(html) => onFieldChange(type, 'textAr', html)}
                            placeholder="Arabic terms (rich text)"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="primary"
                      disabled={!canSave}
                      onClick={() => onSave(type)}
                      loading={isChangeLoading}
                    >
                      Save
                    </Button>
                  </div>
                </Card>
              </Col>
            )
          })}
        </Row>
      </div>
    </div>
  )
}

export default TermsConditions
